// Wowza servers API
import { getAPI, token } from "@/api/params.js";
import { parseStreamData } from "@/api/streams.js";
import { parseObjectNumberProps } from "@/utils/parsing";
import { checkToken } from "@/api/auth.js";

const bytesInGb = 1024 * 1024 * 1024;

export const actionsLog = async () => {
  const getLogUrl = `${getAPI()}/action-logs`;

  const token = await checkToken();
  const resp = await fetch(getLogUrl, {
    headers: { Authorization: `Bearer ${token}` }
  });
  const logList = await resp.json();
  return logList.items;
};

/**
 * Get Wowza servers list from backend server
 */
export const getServersList = async () => {
  const getServersURL = `${getAPI()}/worker`;
  const getStatusURL = `${getAPI()}/worker/status`;

  const token = await checkToken();
  if (!token) {
    // throw Error("Unauthorized");
    console.log("LOGOUT");
    // this.$router.push("/adminPage");
  }
  /** Make two requests (main info and additional) and wait till both will be resolved */
  const [respServers, respStatus] = await Promise.all([
    fetch(getServersURL, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }),
    fetch(getStatusURL, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
  ]);

  /** Errors handling */
  try {
    var serversData = await respServers.json();
  } catch {
    throw new Error(`${respServers.status}: ${respServers.responseText}`);
  }

  try {
    var statusData = await respStatus.json();
  } catch {
    throw new Error(`${respStatus.status}: ${respStatus.responseText}`);
  }

  if ("success" in serversData && !serversData.success) {
    throw new Error(serversData.error);
  }

  if ("success" in statusData && !statusData.success) {
    throw new Error(statusData.error);
  }

  /** Merge two requests information */
  const mergedServersInfo = mergeServersInfo(serversData.items, statusData);

  /** Data parsing */
  const servers = mergedServersInfo.map(obj => parseServerData(obj));

  return servers;
};

/**
 * Convert server information to frontend application format
 * @param {Object} data Server information received from backend
 */
function parseServerData(data) {
  const {
    heapUsed,
    memoryFree,
    memoryUsed,
    cpuUser,
    diskUsed,
    diskFree
  } = parseObjectNumberProps(data);

  /** Convert server data to frontend format */
  const memoryUsage = heapUsed / bytesInGb; // convert from bytes to gigabytes
  const fullMemory = (memoryFree + memoryUsed) / bytesInGb; // calculate full memory and convert
  const cpuUsage = cpuUser / 100; // scale from 0-100 to 0-1
  const diskUsage = diskUsed / bytesInGb; // convert from bytes to gigabytes
  const fullDisk = (diskUsed + diskFree) / bytesInGb; // calculate full disk size and convert

  const workerHostUrl =
    data.worker_api_protocol && data.worker_host
      ? `${data.worker_api_protocol}://${data.worker_host}`
      : null;
  const managerUrl =
    workerHostUrl && data.worker_manager_port
      ? `${workerHostUrl}:${data.worker_manager_port}`
      : null;
  const apiUrl =
    workerHostUrl && data.worker_port
      ? `${workerHostUrl}:${data.worker_port}`
      : null;
  return {
    data: data,
    // sourceStatus: data.streamChannel[0].last_source_status,
    id: data.id,
    name: `${data.worker_host}`,
    managerUrl: managerUrl ?? "No URL",
    apiUrl: apiUrl ?? "No URL",
    active: Boolean(data.online),
    memoryUsage: memoryUsage || 0,
    fullMemory: fullMemory || 0,
    cpuUsage: cpuUsage || 0,
    diskUsage: diskUsage || 0,
    fullDisk: fullDisk || 0,
    streams: data.streamChannel.map(stream => parseStreamData(stream, data)),
    uptime: data.serverUptime || 0
  };
}

/**
 * Merge general servers information and detailed servers statuses into one array
 * @param {Array} servers Main servers data
 * @param {Array} statuses Detailed servers statuses
 */
function mergeServersInfo(servers, statuses) {
  const merged = [];
  servers.forEach(serv => {
    const stat = statuses.find(st => st.workerInstances.id == serv.id);

    if (stat) {
      merged.push({
        ...stat,
        ...serv
      });
    } else {
      merged.push({ ...serv });
    }
  });

  return merged;
}
